import { useEffect, useRef } from 'react';
import { getFirebaseAuth, getFirebaseApp } from '../auth/firebase';
import type { ConnectableSource } from '../domain/providerIds';
import { decodeSourceSyncResourceKey } from '@zunelo/shared';

type Change = { generation?: number };
type Snapshot = { changes?: Record<string, Change> };
export const AI_REVIEW_SYNC_UNAVAILABLE = 'AI_REVIEW_SYNC_UNAVAILABLE' as const;
export const AI_REVIEW_SYNC_UNAVAILABLE_MESSAGE =
  'Live review status could not be refreshed. Retry status.';

function sourceFromResourceKey(key: string): string | undefined {
  return decodeSourceSyncResourceKey(key)?.namespace;
}

const RELOADABLE_SOURCES = new Set<ConnectableSource>(['gmail', 'slack', 'jira']);

function notifyReconciler(source: string, key: string): void {
  const reconciler = reconcilers[source];
  if (!reconciler) return;
  void reconciler(key).catch((error: unknown) => {
    notifySourceSyncError(
      {
        code: AI_REVIEW_SYNC_UNAVAILABLE,
        message: AI_REVIEW_SYNC_UNAVAILABLE_MESSAGE,
        cause: error,
      },
      source,
    );
  });
}

export function applySourceSyncChange(
  key: string,
  change: Change,
  last: Map<string, number>,
  pending: Set<ConnectableSource>,
): void {
  const source = sourceFromResourceKey(key);
  const generation = change.generation ?? 0;
  if (!source || generation <= (last.get(key) ?? 0)) return;
  notifyReconciler(source, key);
  if (RELOADABLE_SOURCES.has(source as ConnectableSource)) pending.add(source as ConnectableSource);
  last.set(key, generation);
}

export type SourceSyncReconciler = (resourceKey: string) => Promise<void>;
export interface SourceSyncError {
  code: typeof AI_REVIEW_SYNC_UNAVAILABLE;
  message: string;
  cause?: unknown;
}
export type SourceSyncErrorHandler = (error: SourceSyncError) => void;
const reconcilers: Record<string, SourceSyncReconciler> = {};
const errorHandlers: Record<string, SourceSyncErrorHandler> = {};

export function registerSourceSyncReconciler(
  namespace: string,
  handler: SourceSyncReconciler,
): () => void {
  reconcilers[namespace] = handler;
  return () => {
    if (reconcilers[namespace] === handler) delete reconcilers[namespace];
  };
}

export function registerSourceSyncErrorHandler(
  namespace: string,
  handler: SourceSyncErrorHandler,
): () => void {
  errorHandlers[namespace] = handler;
  return () => {
    if (errorHandlers[namespace] === handler) delete errorHandlers[namespace];
  };
}

export function notifySourceSyncError(error: SourceSyncError, namespace?: string): void {
  const handlers = namespace ? [errorHandlers[namespace]] : Object.values(errorHandlers);
  handlers.forEach((handler) => handler?.(error));
}

export function useSourceSyncIndex(reload: (source: ConnectableSource) => Promise<void>): void {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let active = true;
    let last = new Map<string, number>();
    const subscribe = async (): Promise<void> => {
      if (!active || document.hidden) return;
      const user = getFirebaseAuth().currentUser;
      if (!user) return;
      const { doc, onSnapshot, getFirestore } = await import('firebase/firestore');
      if (!active || document.hidden) return;
      unsubscribe?.();
      unsubscribe = onSnapshot(
        doc(getFirestore(getFirebaseApp()), 'source_sync', user.uid),
        (snapshot) => {
          const data = snapshot.data() as Snapshot | undefined;
          const pending = new Set<ConnectableSource>();
          Object.entries(data?.changes ?? {}).forEach(([key, change]) =>
            applySourceSyncChange(key, change, last, pending),
          );
          for (const source of pending) void reloadRef.current(source);
        },
        (error) =>
          notifySourceSyncError({
            code: AI_REVIEW_SYNC_UNAVAILABLE,
            message: AI_REVIEW_SYNC_UNAVAILABLE_MESSAGE,
            cause: error,
          }),
      );
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        unsubscribe?.();
        unsubscribe = undefined;
      } else {
        last = new Map();
        void subscribe().catch(() => undefined);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    void subscribe().catch(() => undefined);
    return () => {
      active = false;
      unsubscribe?.();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}
